import { CERTIFICATION_COURSES } from '@common/constants/certification.constants'
import {
	CertificationFormValues,
	TraineeCertification
} from '@common/types/certification.types'
import { useCertificationApi } from '@services/api/certification.api'
import { useCertification } from '@services/contexts/CertificationContext'
import { useSnackbar } from '@services/contexts/SnackbarContext'
import { useMutation } from '@tanstack/react-query'
import {
	FormProvider,
	useForm
} from 'react-hook-form'
import { useIntl } from 'react-intl'
import CertificationPopIn from './CertificationPopIn'

type CertificationCreationPopInProps = {
	isOpen: boolean
	onClose: () => void
	onSuccess: (data: TraineeCertification) => void
	traineeId: string
}

// TODO #17 RG431, RG436

/**
 * Certification creation pop-in
 * @param isOpen
 * @param onClose
 * @param onSuccess
 * @param traineeId
 * @constructor
 */
const CertificationCreationPopIn = (
	{
		isOpen,
		onClose,
		onSuccess,
		traineeId
	}: CertificationCreationPopInProps
) => {
	const { certification, setCertification } = useCertification()
	const { formatMessage } = useIntl()
	const { createCertification, downloadCertification } = useCertificationApi()
	const { addErrorSnackbar, addSnackbar } = useSnackbar()

	const form = useForm<CertificationFormValues>({
		defaultValues: {
			course: certification.course || CERTIFICATION_COURSES[0],
			obtaining: {
				isTrainer: certification.obtaining.isTrainer
			},
			date: certification.date || new Date().toISOString(),
			trainerId: certification.trainerId
		}
	})

	const { mutate: downloadCertificationMutate } = useMutation<void, Error, string>({
		mutationKey: ['downloadCertification'],
		mutationFn: (certificationId) => downloadCertification(certificationId),
		onError: (error) => addErrorSnackbar(error.message)
	})

	const { mutate: createCertificationMutate } = useMutation<TraineeCertification, Error, CertificationFormValues>({
		mutationKey: ['createCertification', traineeId],
		mutationFn: (values) => createCertification(traineeId, values),
		onSuccess: (data) => {
			onSuccess(data)
			setCertification({
				course: data.course,
				date: data.trainedOn,
				obtaining: {
					isTrainer: data.isTrainer
				},
				trainerId: data.trainerId
			})
			downloadCertificationMutate(data.id)
			addSnackbar(formatMessage({ id: 'components.certificationPopIn.creation.success' }))
		},
		onError: (error) => addErrorSnackbar(error.message)
	})

	const onSubmit = (values: CertificationFormValues) => createCertificationMutate(values)

	return (
		<FormProvider {...form}>
			<CertificationPopIn
				title={formatMessage({ id: 'components.certificationPopIn.creation.title' })}
				subtitle={formatMessage({ id: 'components.certificationPopIn.creation.subtitle' })}
				buttons={[{
					label: formatMessage({ id: 'components.certificationPopIn.creation.buttons.confirm.label' }),
					onClick: form.handleSubmit(onSubmit)
				}]}
				traineeId={traineeId}
				onClose={onClose}
				isOpen={isOpen}
			/>
		</FormProvider>
	)
}

export default CertificationCreationPopIn
